import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  ChartBarIcon,
  PresentationChartLineIcon,
  CalendarDaysIcon,
  CurrencyRupeeIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
// Import Recharts components
import {
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell, 
  Label
} from 'recharts';

// Removed unused Structure interface
//   type: string;
//   location: string;
//   status: string;
//   isLED: boolean;
// }

interface StructureStats {
  id: number;
  code: string;
  type: string;
  location: string;
  occupancyRate: number;
  revenue: number;
  orderCount: number;
  avgDuration: number;
  popularityRank: number; // Added popularity rank
}

// Added interface for summary data
interface ReportSummary {
  totalRevenue: number;
  averageOccupancyRate: number;
  totalStructures: number;
  totalOrders: number;
}

// Added interface for top structures list items
interface TopStructure {
  id: number;
  code: string;
  orderCount?: number; // Optional for profitable list
  revenue?: number; // Optional for popular list
}

const StructureReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [structureStats, setStructureStats] = useState<StructureStats[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(null); // State for summary
  const [mostPopular, setMostPopular] = useState<TopStructure[]>([]); // State for most popular
  const [mostProfitable, setMostProfitable] = useState<TopStructure[]>([]); // State for most profitable
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [isExporting, setIsExporting] = useState(false); // Loading state for export

  // Prepare data for charts using useMemo to avoid recalculation on every render
  const chartData = useMemo(() => {
    if (!structureStats || structureStats.length === 0) {
      return {
        revenueByType: [],
        topProfitable: [],
        topPopular: [],
      };
    }

    // 1. Revenue by Type (LED vs Static)
    let totalLedRevenue = 0;
    let totalStaticRevenue = 0;
    structureStats.forEach(stat => {
      // Assuming type contains 'LED' for LED screens
      if (stat.type.toUpperCase().includes('LED')) {
        totalLedRevenue += stat.revenue;
      } else {
        totalStaticRevenue += stat.revenue;
      }
    });
    const revenueByType = [
      { name: 'LED', value: parseFloat(totalLedRevenue.toFixed(2)) },
      { name: 'Статика', value: parseFloat(totalStaticRevenue.toFixed(2)) },
    ].filter(item => item.value > 0); // Filter out types with zero revenue

    // 2. Top 5 Profitable (Data already fetched as mostProfitable)
    const topProfitable = mostProfitable.map(s => ({
      name: s.code,
      Доход: s.revenue
    }));

    // 3. Top 5 Popular (Data already fetched as mostPopular)
    const topPopular = mostPopular.map(s => ({
      name: s.code,
      Заказы: s.orderCount
    }));

    return { revenueByType, topProfitable, topPopular };

  }, [structureStats, mostProfitable, mostPopular]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set default date range if not selected
        const end = new Date();
        const start = new Date();
        
        if (period === 'month') {
          start.setMonth(end.getMonth() - 1);
        } else if (period === 'quarter') {
          start.setMonth(end.getMonth() - 3);
        } else if (period === 'year') {
          start.setFullYear(end.getFullYear() - 1);
        }
        
        const formattedStartDate = startDate || start.toISOString().split('T')[0];
        const formattedEndDate = endDate || end.toISOString().split('T')[0];
        
        // Fetch structure statistics
        // const structuresResponse = await axios.get('/structures'); // Removed unused fetch
        const statsResponse = await axios.get('/reports/structures', {
          params: {
            startDate: formattedStartDate,
            endDate: formattedEndDate
          }
        });

        // Extract data from response
        const { summary, mostPopularStructures, mostProfitableStructures, structures } = statsResponse.data;

        setStructureStats(structures || []); // Set the detailed structure stats
        setReportSummary(summary || null); // Set the overall summary
        setMostPopular(mostPopularStructures || []); // Set top popular structures
        setMostProfitable(mostProfitableStructures || []); // Set top profitable structures

        // Update date inputs with actual dates used
        if (!startDate) setStartDate(formattedStartDate);
        if (!endDate) setEndDate(formattedEndDate);
      } catch (error) {
        console.error('Error fetching structure report data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [period]); // Added startDate and endDate dependencies
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update state, useEffect will trigger fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    setIsExporting(true); // Use existing loading state

    // Construct the URL for the print page with query parameters
    const params = new URLSearchParams({ startDate, endDate });
    if (filterType !== 'all') {
      params.append('structureTypes', filterType); // Pass filter if not 'all'
    }
    const printUrl = `/reports/structures/print?${params.toString()}`;

    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }

    // Reset loading state after a short delay
    setTimeout(() => setIsExporting(false), 1000);
  };

  // Filter stats based on structure type
  const filteredStats = structureStats.filter(stat => {
    if (filterType === 'all') return true;
    if (filterType === 'led') return stat.type.includes('LED');
    return !stat.type.includes('LED');
  });
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Calculate totals
  const totalRevenue = filteredStats.reduce((sum, item) => sum + item.revenue, 0);
  const totalOrders = filteredStats.reduce((sum, item) => sum + item.orderCount, 0);
  const avgOccupancy = filteredStats.length > 0
    ? filteredStats.reduce((sum, item) => sum + item.occupancyRate, 0) / filteredStats.length
    : 0;
  
  // Define colors for Pie chart
  const PIE_CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#00C49F'];

  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Отчет по конструкциям"
        subtitle={`Статистика по конструкциям за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            disabled={isExporting || !startDate || !endDate} // Disable if exporting or dates missing
            className="btn-secondary flex items-center disabled:opacity-50"
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            {isExporting ? 'Открытие...' : 'Печать'} {/* Update text */}
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
          
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="form-input"
            >
              <option value="all">Все типы</option>
              <option value="led">LED экраны</option>
              <option value="cityboard">Ситиборды</option>
            </select>
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Summary Section */} 
      {reportSummary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <h3 className="text-sm font-medium text-gray-500">Общий доход</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(reportSummary.totalRevenue)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <h3 className="text-sm font-medium text-gray-500">Средняя занятость</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{reportSummary.averageOccupancyRate.toFixed(1)}%</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <h3 className="text-sm font-medium text-gray-500">Всего конструкций</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{reportSummary.totalStructures}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <h3 className="text-sm font-medium text-gray-500">Всего заказов</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{reportSummary.totalOrders}</p>
          </div>
        </div>
      )}

      {/* Charts Section */} 
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Top 5 Profitable Chart */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 text-center">Топ 5 по доходу</h3>
          {chartData.topProfitable.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.topProfitable} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatCurrency} />
                <YAxis dataKey="name" type="category" width={60} />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                {/* <Legend /> */}
                <Bar dataKey="Доход" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-10">Нет данных для отображения</p>
          )}
        </div>

        {/* Top 5 Popular Chart */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 text-center">Топ 5 по заказам</h3>
          {chartData.topPopular.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.topPopular} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis dataKey="name" type="category" width={60} />
                <Tooltip />
                {/* <Legend /> */}
                <Bar dataKey="Заказы" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
           ) : (
            <p className="text-center text-gray-500 py-10">Нет данных для отображения</p>
          )}
        </div>

        {/* Revenue by Type Pie Chart */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-4 text-center">Доход по типу</h3>
          {chartData.revenueByType.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.revenueByType}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.revenueByType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                {/* <Legend /> */}
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-10">Нет данных для отображения</p>
          )}
        </div>
      </div>

      {/* Detailed Stats Table */} 
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <h3 className="text-lg font-semibold p-4 border-b">Детальная статистика по конструкциям</h3>
        {filteredStats.length === 0 ? (
          <p className="p-6 text-center text-gray-500">Нет данных за выбранный период или по выбранному типу.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              {/* ... (Existing table thead and tbody) ... */}
              {/* Ensure all relevant columns from StructureStats are displayed */}
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Код</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Тип</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Адрес</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Занятость (%)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Доход</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кол-во заказов</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ср. длит. (дни)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ранг</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStats.map((stat) => (
                  <tr key={stat.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{stat.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{stat.type}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{stat.location}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">{stat.occupancyRate.toFixed(1)}%</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">{formatCurrency(stat.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">{stat.orderCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">{stat.avgDuration}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right">{stat.popularityRank}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StructureReport;
